/**
 * Locale (IETF BCP 47) source of truth.
 *
 * The single embedded dataset is `utils/countries.csv` — its `language_codes` column
 * holds real BCP 47 tags (e.g. `en-GB`, `es-AR`, `zh-CN`). This module parses that CSV
 * once at load and exposes:
 *   - the strict allowlist (`SUPPORTED_LOCALE_SET`) used to validate what we WRITE,
 *   - the per-dialect picker options (`LOCALE_OPTIONS`),
 *   - `normalizeLocale()` to coerce any legacy/loose input into a valid tag on write,
 *   - read-tolerant display helpers that never throw on un-migrated values.
 *
 * Canonical default is `en-GB`. See the approved plan for scope: this file governs the
 * VALUE the repo sets; downstream (AI/N8N) consumption is out of scope.
 */

// Vite inlines the CSV as a string at build time (see csv-raw.d.ts for the type).
import countriesCsv from '../utils/countries.csv?raw';

export type Locale = string;

export const DEFAULT_LOCALE = 'en-GB';

export interface LocaleOption {
  code: string; // BCP 47 tag, e.g. "es-AR"
  language: string; // e.g. "Spanish"
  country: string; // e.g. "Argentina"
  flag: string; // emoji, may be ""
  label: string; // language-first, e.g. "Spanish (Argentina)"
  display: string; // "🇦🇷 Spanish (Argentina)" — searchable text used as the <datalist> value
}

/**
 * Curated language-name -> canonical locale, used ONLY by normalizeLocale to give a
 * legacy bare input (e.g. "spanish", "en") a sensible default region. It does NOT drive
 * the picker (which stores exact regional tags). Keys are lowercased language names.
 * Needed where the "region subtag == language subtag" heuristic doesn't hold
 * (e.g. Japanese ja-JP, English en-GB) or to pin a preferred region.
 */
const CANONICAL_OVERRIDES: Record<string, string> = {
  english: 'en-GB',
  spanish: 'es-ES',
  portuguese: 'pt-PT',
  french: 'fr-FR',
  german: 'de-DE',
  arabic: 'ar-SA',
  chinese: 'zh-CN',
  japanese: 'ja-JP',
  korean: 'ko-KR',
  hindi: 'hi-IN',
  swedish: 'sv-SE',
  norwegian: 'nb-NO',
  'norwegian bokmål': 'nb-NO',
  danish: 'da-DK',
  greek: 'el-GR',
  ukrainian: 'uk-UA',
  czech: 'cs-CZ',
  polish: 'pl-PL',
  turkish: 'tr-TR',
  hebrew: 'he-IL',
  vietnamese: 'vi-VN',
  thai: 'th-TH',
  indonesian: 'id-ID',
};

// A valid BCP 47 tag that is not tied to a single country (UN M49 region 419).
// Not present in the country CSV, so added manually to give the legacy
// "Spanish Latino" dropdown value a home.
const ES_419: LocaleOption = {
  code: 'es-419',
  language: 'Spanish',
  country: 'Latin America',
  flag: '🌎',
  label: 'Spanish (Latin America)',
  display: '🌎 Spanish (Latin America)',
};

// Minimal fallback used only if CSV parsing fails, so importers never crash on load.
const FALLBACK_CODES = ['en-GB', 'en-US', 'es-ES', 'fr-FR', 'de-DE', 'pt-PT', 'it-IT', 'nl-NL', 'sv-SE'];

/** Parse a single CSV line, honoring double-quoted fields that may contain commas. */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(field);
      field = '';
    } else {
      field += ch;
    }
  }
  out.push(field);
  return out;
}

interface ParsedLocales {
  supported: Set<string>;
  options: LocaleOption[];
  toLanguage: Map<string, string>;
  toLabel: Map<string, string>;
  languageNameToCanonical: Map<string, string>; // "spanish" -> "es-ES"
  languageSubtagToCanonical: Map<string, string>; // "es" -> "es-ES"
  lowerToCanonical: Map<string, string>; // "es-ar" -> "es-AR"
  displayToCode: Map<string, string>; // "🇦🇷 spanish (argentina)" (lowercased) -> "es-AR"
}

function parseCountriesCsv(csv: string): ParsedLocales {
  const supported = new Set<string>();
  const options: LocaleOption[] = [];
  const toLanguage = new Map<string, string>();
  const toLabel = new Map<string, string>();
  const lowerToCanonical = new Map<string, string>();
  const langToCodes = new Map<string, string[]>(); // lowercased language name -> codes[]

  const lines = csv.split(/\r?\n/);
  // Column order: name,alpha2,ccTLD,countryCallingCodes,currencies,emoji,ioc,language_codes,language_names,timezones
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const cols = parseCsvLine(line);
    const country = (cols[0] ?? '').trim();
    const flag = (cols[5] ?? '').trim();
    const codesRaw = (cols[7] ?? '').trim();
    const namesRaw = (cols[8] ?? '').trim();
    if (!codesRaw) continue; // skip rows with no language (Antarctica, EU, ...)

    const codes = codesRaw.split(',').map((c) => c.trim()).filter(Boolean);
    const names = namesRaw.split(',').map((n) => n.trim());

    codes.forEach((code, idx) => {
      const language = (names[idx] ?? '').trim() || code;
      const isNew = !supported.has(code);
      supported.add(code);
      lowerToCanonical.set(code.toLowerCase(), code);

      const nameKey = language.toLowerCase();
      if (!langToCodes.has(nameKey)) langToCodes.set(nameKey, []);
      langToCodes.get(nameKey)!.push(code);

      if (isNew) {
        const label = `${language} (${country})`;
        const display = `${flag} ${label}`.trim();
        options.push({ code, language, country, flag, label, display });
        toLanguage.set(code, language);
        toLabel.set(code, label);
      }
    });
  }

  // es-419 (manual addition)
  if (!supported.has(ES_419.code)) {
    supported.add(ES_419.code);
    lowerToCanonical.set(ES_419.code.toLowerCase(), ES_419.code);
    options.push(ES_419);
    toLanguage.set(ES_419.code, ES_419.language);
    toLabel.set(ES_419.code, ES_419.label);
  }

  // Resolve one canonical locale per language name (legacy-normalize path only).
  const languageNameToCanonical = new Map<string, string>();
  const languageSubtagToCanonical = new Map<string, string>();
  for (const [name, codes] of langToCodes) {
    let canonical: string | undefined = CANONICAL_OVERRIDES[name];
    if (!canonical) {
      // Prefer the "homeland" tag where language subtag == region subtag (fr-FR, de-DE).
      canonical = codes.find((c) => {
        const parts = c.toLowerCase().split('-');
        return parts[0] === parts[parts.length - 1];
      });
    }
    if (!canonical) canonical = codes[0];
    languageNameToCanonical.set(name, canonical);

    const subtag = canonical.split('-')[0].toLowerCase();
    // Overrides win; otherwise first writer keeps the subtag mapping.
    if (CANONICAL_OVERRIDES[name] || !languageSubtagToCanonical.has(subtag)) {
      languageSubtagToCanonical.set(subtag, canonical);
    }
  }

  options.sort((a, b) => a.language.localeCompare(b.language) || a.country.localeCompare(b.country));

  const displayToCode = new Map<string, string>();
  for (const opt of options) displayToCode.set(opt.display.toLowerCase(), opt.code);

  return { supported, options, toLanguage, toLabel, languageNameToCanonical, languageSubtagToCanonical, lowerToCanonical, displayToCode };
}

let parsed: ParsedLocales;
try {
  parsed = parseCountriesCsv(countriesCsv);
  if (!parsed.supported.has(DEFAULT_LOCALE)) parsed.supported.add(DEFAULT_LOCALE);
} catch (err) {
  // Never let a bad CSV take down every importer — fall back to a minimal safe set.
  console.error('[locales] Failed to parse countries.csv, using fallback locale set.', err);
  const supported = new Set(FALLBACK_CODES);
  const lowerToCanonical = new Map(FALLBACK_CODES.map((c) => [c.toLowerCase(), c] as const));
  parsed = {
    supported,
    options: FALLBACK_CODES.map((code) => ({ code, language: code, country: '', flag: '', label: code, display: code })),
    toLanguage: new Map(),
    toLabel: new Map(),
    languageNameToCanonical: new Map(),
    languageSubtagToCanonical: new Map(FALLBACK_CODES.map((c) => [c.split('-')[0], c] as const)),
    lowerToCanonical,
    displayToCode: new Map(),
  };
}

/** Every locale we accept on write (strict allowlist). */
export const SUPPORTED_LOCALE_SET: ReadonlySet<string> = parsed.supported;

/** Per-dialect options for the picker, sorted by language then country. */
export const LOCALE_OPTIONS: ReadonlyArray<LocaleOption> = parsed.options;

/** True if `value` is an exact, supported BCP 47 tag. */
export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === 'string' && SUPPORTED_LOCALE_SET.has(value);
}

/**
 * Coerce any input into a valid supported BCP 47 tag for WRITING.
 * Handles: already-valid tags (case-insensitive), bare ISO codes (`en`),
 * English language names (`spanish`), the legacy `X-YY` dropdown values
 * (`English-EN`, `Spanish Latino-ES_LATAM`). Anything unresolvable -> DEFAULT_LOCALE.
 */
export function normalizeLocale(input?: string | null): Locale {
  if (!input) return DEFAULT_LOCALE;
  const raw = String(input).trim();
  if (!raw) return DEFAULT_LOCALE;
  const lower = raw.toLowerCase();

  // 1. Already a known tag (case-insensitive) -> canonical casing.
  const known = parsed.lowerToCanonical.get(lower);
  if (known) return known;

  // 2. Legacy "Spanish Latino" variants.
  if (lower.includes('latino') || lower.includes('es_latam') || lower === 'es-419') {
    return SUPPORTED_LOCALE_SET.has('es-419') ? 'es-419' : DEFAULT_LOCALE;
  }

  // 3. Language name or legacy "English-EN" dropdown value (take the leading token).
  const namePart = lower.split(/[-_]/)[0].trim();
  const byName = parsed.languageNameToCanonical.get(namePart);
  if (byName) return byName;

  // 4. Bare/unlisted BCP 47 tag -> fall back to its language subtag's canonical.
  const langSubtag = lower.split('-')[0];
  const bySubtag = parsed.languageSubtagToCanonical.get(langSubtag);
  if (bySubtag) return bySubtag;

  if (process.env.NODE_ENV !== 'production') {
    console.warn(`[locales] normalizeLocale: unrecognized "${raw}" -> ${DEFAULT_LOCALE}`);
  }
  return DEFAULT_LOCALE;
}

/**
 * Resolve raw picker input into a supported locale. The `<datalist>` uses each option's
 * `display` string ("🇦🇷 Spanish (Argentina)") as its value so it's searchable by country;
 * this maps that back to the code. Falls through to normalizeLocale for typed codes /
 * legacy values. Always returns a supported tag.
 */
export function resolveLocaleInput(input?: string | null): Locale {
  if (!input) return DEFAULT_LOCALE;
  const byDisplay = parsed.displayToCode.get(String(input).trim().toLowerCase());
  if (byDisplay) return byDisplay;
  return normalizeLocale(input);
}

/** The picker `display` string for a stored code, for pre-filling the input (read-tolerant). */
export function getLocaleDisplay(code?: string | null): string {
  if (!code) return '';
  const opt = parsed.options.find((o) => o.code === code);
  return opt ? opt.display : getLocaleLabel(code);
}

/**
 * Curated picker list: the TOP business languages worldwide, max 2 variants
 * per language (main dialects only), plus Spain's co-official languages.
 * Kept to 25 entries so the dropdown stays scannable — the LLM handles
 * translation for everything else. Codes must exist in LOCALE_OPTIONS
 * (es-419 is added manually above).
 */
export const BUSINESS_LOCALE_CODES = [
  'en-GB', 'en-US',                          // English (UK / USA)
  'es-ES', 'es-419',                         // Español (España / Latino)
  'ca-ES',                                   // Català
  'pt-PT', 'pt-BR',                          // Português (PT / BR)
  'fr-FR',                                   // Français
  'de-DE',                                   // Deutsch
  'it-IT', 'nl-NL', 'pl-PL',                 // Italiano, Nederlands, Polski
  'sv-SE', 'nb-NO', 'da-DK', 'fi-FI',        // Svenska, Norsk, Dansk, Suomi
  'ru-RU', 'tr-TR',                          // Русский, Türkçe
  'ar-SA',                                   // العربية
  'zh-CN', 'zh-TW',                          // 中文 (Simplified / Traditional)
  'ja-JP', 'ko-KR', 'hi-IN',                 // 日本語, 한국어, हिन्दी
] as const;

/** The picker's code set, for fast membership checks (e.g. locale detection). */
export const PICKER_LOCALE_CODE_SET: ReadonlySet<string> = new Set<string>(BUSINESS_LOCALE_CODES);

/**
 * Picker options for registration/team dropdowns: ONLY the curated business
 * languages above, in curated order. The full LOCALE_OPTIONS list stays
 * available for other consumers.
 */
export const PICKER_LOCALE_OPTIONS: ReadonlyArray<LocaleOption> = BUSINESS_LOCALE_CODES
  .map((code) => LOCALE_OPTIONS.find((opt) => opt.code === code))
  .filter((opt): opt is LocaleOption => Boolean(opt));

/** Display label for a code, passing legacy/unknown values through unchanged (read-tolerant). */
export function getLocaleLabel(code?: string | null): string {
  if (!code) return '';
  return parsed.toLabel.get(code) ?? code;
}

/** Language name for a code, passing legacy/unknown values through unchanged (read-tolerant). */
export function getLanguageName(code?: string | null): string {
  if (!code) return '';
  return parsed.toLanguage.get(code) ?? code;
}
